import * as React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { fieldClasses } from "@/components/ui/input";
import { useMentionAutocomplete, type MentionCandidate } from "@/components/mention-autocomplete";
import { cn } from "@/lib/utils";

/**
 * The text box every chat composer here uses: agent chats, the master, a card's
 * thread, the intake chat, a reply's rewrite box.
 *
 * Enter (and Shift+Enter) is a newline, always. Sending is the Send button, or
 * Ctrl+Enter (⌘+Enter on a Mac) — what people write here is instructions and
 * lists, and a message that went half-written because a line break was typed
 * was the complaint. Nothing is sent while an IME is composing, where Enter
 * means "accept this candidate".
 *
 * It grows with its text up to `maxHeight`, then scrolls. Given `mentions`, typing
 * `@` opens the agent picker (`mention-autocomplete.tsx`); while that is open,
 * Enter and Tab pick rather than break the line.
 *
 * The textarea sits in a `relative` wrapper (for the popup): layout classes such
 * as `flex-1` belong on `wrapperClassName`.
 */
export interface ComposerTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Ctrl/⌘+Enter. */
  onSend?: () => void;
  /** Pixels, or a function of the window height. Default: a third of the window. */
  maxHeight?: number | ((windowHeight: number) => number);
  /** Use the form-field look (border, focus ring) rather than a bare box inside a card. */
  field?: boolean;
  /** Who `@` can reach from here; omit for no autocomplete. */
  mentions?: MentionCandidate[];
  /** Where the picker opens. Above suits a composer pinned over the keyboard. */
  mentionPlacement?: "above" | "below";
  wrapperClassName?: string;
}

export const ComposerTextarea = React.forwardRef<HTMLTextAreaElement, ComposerTextareaProps>(
  (
    { onSend, maxHeight, field = false, mentions, mentionPlacement, wrapperClassName, className, onKeyDown, value, ...props },
    forwarded,
  ) => {
    const inner = useRef<HTMLTextAreaElement | null>(null);
    const setRef = (node: HTMLTextAreaElement | null) => {
      inner.current = node;
      if (typeof forwarded === "function") forwarded(node);
      else if (forwarded) forwarded.current = node;
    };
    useAutoGrow(inner, value, maxHeight);
    const autocomplete = useMentionAutocomplete(inner, value, mentions, mentionPlacement);

    return (
      <div className={cn("relative", wrapperClassName)}>
      {autocomplete.popup}
      <textarea
        ref={setRef}
        value={value}
        // What ⌘⇧K / Ctrl+Shift+K looks for first (`lib/focus-input.ts`).
        data-composer=""
        className={cn(
          field
            ? cn(fieldClasses, "py-2 leading-relaxed")
            : "bg-transparent outline-none placeholder:text-muted-foreground",
          "block w-full resize-none overflow-y-auto scrollbar-slim",
          className,
        )}
        aria-expanded={mentions ? autocomplete.open : undefined}
        onKeyDown={(event) => {
          if (autocomplete.onKeyDown(event)) return;
          onKeyDown?.(event);
          if (event.defaultPrevented) return;
          if (isSendKey(event)) {
            event.preventDefault();
            onSend?.();
          }
        }}
        {...props}
      />
      </div>
    );
  },
);
ComposerTextarea.displayName = "ComposerTextarea";

/**
 * The round Send button's look in every composer. Disabled it is a grey chip rather
 * than a faded primary one: faded primary read as "disabled" even once there was
 * text to send.
 */
export const SEND_BUTTON_CLASS =
  "rounded-full disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 disabled:shadow-none";

/** Ctrl/⌘+Enter, and never mid-composition. */
export function isSendKey(event: React.KeyboardEvent): boolean {
  return event.key === "Enter" && (event.ctrlKey || event.metaKey) && !event.nativeEvent.isComposing;
}

/**
 * Sizes the box to its content, capped. Runs before paint so a line break never
 * shows a scrollbar for a frame.
 */
export function useAutoGrow(
  ref: React.RefObject<HTMLTextAreaElement | null>,
  value: unknown,
  maxHeight?: number | ((windowHeight: number) => number),
) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cap =
      typeof maxHeight === "number" ? maxHeight : maxHeight ? maxHeight(window.innerHeight) : window.innerHeight / 3;
    el.style.height = "auto";
    // scrollHeight excludes the border; add it back so the last line is not clipped.
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(el.scrollHeight + border, cap)}px`;
  }, [ref, value, maxHeight]);
}

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

/** True on a mouse/trackpad device — the ones that come with a hardware keyboard. */
export function useFinePointer(): boolean {
  const [fine, setFine] = useState(() => typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)").matches);
  useEffect(() => {
    const query = window.matchMedia?.("(pointer: fine)");
    if (!query) return;
    const update = () => setFine(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return Boolean(fine);
}

/** "Ctrl+Enter to send" (⌘↵ on a Mac), shown only where there is a hardware keyboard. */
export function SendHint({ verb = "send", className }: { verb?: string; className?: string }) {
  const fine = useFinePointer();
  if (!fine) return null;
  return (
    <span className={cn("shrink-0 text-[10.5px] text-muted-foreground", className)}>
      <kbd className="font-sans">{IS_MAC ? "⌘↵" : "Ctrl+Enter"}</kbd> to {verb}
    </span>
  );
}
